import React, { useState } from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';
import { createStackNavigator, TransitionPresets } from '@react-navigation/stack';

import { scale, notchTop, width } from '@components/frame';
import { isPhone, Popup, getStateParams, navigateTo } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import ColorWrapper from '@styles/colors';
import Selector from '@styles/selectors';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';

import BackButton from '@components/common/backButton';

import Machines from './machines';
import MachinesGroup from './machinesGroup';
import MachinesOperations from './machinesOperations';

import AddMachineGroupName from './addMachineGroupName';
import {
  BACK_BUTTON_SETTINGS_TEXT,
  NAVIGATION_CONFIG,
  SCREEN_PADDING_HORIZONTAL,
  SMART_FACTORY,
} from '../../../../../v2/pages/common/constants/base.constants';
import MachinesImnoo from './machinesImnoo';
import { useSelector } from 'react-redux';
import { selectedMachinesSettingsScrollX, setMachinesSettingsScrollX } from '@reducers/header';
import { useHandleScroll } from '@v2/pages/common/hooks/useHandleScroll';

const StackNavigator = createStackNavigator();

const Header = ({ level, navigate, toggleAddMachine, toggleCreateMachinesGroup, C }) => {
  const isWithERP = useSelector((state) => state.membership.isWithERP);

  const levels = [
    {
      name: 'Machines',
      key: 'list',
      link: 'MachinesList',
    },
    {
      name: 'Machine Groups',
      key: 'group',
      link: 'MachinesGroup',
    },
    ...(isWithERP
      ? [
          {
            name: 'Operations',
            key: 'operations',
            link: 'MachinesOperations',
          },
        ]
      : []),
    {
      name: 'Imnoo',
      key: 'imnoo',
      link: 'MachinesImnoo',
    },
  ];

  const actions = {
    list: {
      name: 'ADD MACHINE',
      action: toggleAddMachine,
    },
    group: {
      name: 'ADD MACHINE GROUP',
      action: toggleCreateMachinesGroup,
    },
  };

  const header = {
    list: 'Machines',
    group: 'Machine Groups',
    operations: 'Operations',
    imnoo: 'Imnoo',
  };

  const machinesSettingsScrollX = useSelector(selectedMachinesSettingsScrollX);

  const { handleScroll, handleScrollEnd, scrollViewRef } = useHandleScroll(setMachinesSettingsScrollX, machinesSettingsScrollX);

  const LevelsContent = (
    <>
      {levels.map((item) => (
        <View key={item.key}>
          <Selector
            {...{
              selected: level === item.key,
              variant: 'L',
              text: i18n.t(item.name),
              onPress: () => {
                navigate('Settings', {
                  screen: 'Machines',
                  params: {
                    screen: 'MachinesScreen',
                    params: { screen: item.link },
                  },
                });
              },
            }}
          />
        </View>
      ))}
    </>
  );

  const Levels = () => (
    <>
      {!isPhone ? (
        <View style={{ flex: 1, flexDirection: 'row', justifyContent: 'center' }}>{LevelsContent}</View>
      ) : (
        <ScrollView
          ref={scrollViewRef}
          onScroll={handleScroll}
          onScrollEndDrag={handleScrollEnd}
          onMomentumScrollEnd={handleScrollEnd}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={
            ({ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginHorizontal: 'auto' },
            isPhone && { paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) })
          }
        >
          {LevelsContent}
        </ScrollView>
      )}
    </>
  );

  const onBackButtonPress = () => {
    navigateTo('Settings', {
      screen: 'Account',
    });
  };

  return (
    <View style={{ width: '100%', paddingTop: notchTop }}>
      <View
        style={{
          width: '100%',
          height: scale(29),
          marginVertical: scale(10),
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: scale(18),
          marginTop: scale(26),
        }}
      >
        <View style={{ flex: 1 }}>
          {isPhone && (
            <View>
              <BackButton {...{ left: 0, top: 0, padding: 0, text: BACK_BUTTON_SETTINGS_TEXT, onPress: onBackButtonPress }} />
            </View>
          )}
        </View>
        <View style={{ width: width / (isPhone ? 2 : 3), flexDirection: 'row', justifyContent: 'center' }}>
          {isPhone ? (
            <Text style={[font['Headline'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>{i18n.t(header[level])}</Text>
          ) : (
            <Levels />
          )}
        </View>
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginRight: scale(18) }}>
            {actions[level] && (
              <TouchableElement
                onPress={() => {
                  actions[level].action();
                }}
              >
                {({ pressHoverColor, boxStyle }) => (
                  <View
                    style={[
                      {
                        backgroundColor: C(pressHoverColor('Button/Light/Primary')),
                        width: scale(28),
                        height: scale(28),
                        borderRadius: scale(14),
                        justifyContent: 'center',
                        alignContent: 'center',
                        alignItems: 'center',
                      },
                      boxStyle,
                    ]}
                  >
                    <Svg icon="plus" width={scale(14)} height={scale(14)} color={'Label/Light/AlwaysLight Primary'} />
                  </View>
                )}
              </TouchableElement>
            )}
          </View>
        </View>
      </View>
      {isPhone && (
        <View style={{ width: '100%', height: scale(32), marginBottom: scale(18) }}>
          <Levels />
        </View>
      )}
    </View>
  );
};

const MachinesPage = (props) => {
  const { C, toggleAddMachine, navigation, route } = props;

  const toggleCreateMachinesGroup = () => {
    Popup({
      Component: AddMachineGroupName,
      width: 480,
    });
  };

  const setup = { animationEnabled: false };

  return (
    <View
      key="MachineView"
      style={{
        width: '100%',
        backgroundColor: C('Background/Light/Detail'),
        height: '100%',
      }}
    >
      <StackNavigator.Navigator
        screenOptions={{
          ...NAVIGATION_CONFIG,
          cardStyle: {
            backgroundColor: C('Background/Light/Detail'),
          },
          ...setup,
        }}
        initialRouteName={'MachinesList'}
      >
        <StackNavigator.Screen name={'MachinesList'} options={{ title: `Machines List - ${SMART_FACTORY}` }}>
          {(p) => (
            <View style={{ width: '100%', flex: 1 }}>
              <Header
                {...{
                  toggleAddMachine,
                  toggleCreateMachinesGroup,
                  C,
                  navigate: navigation.navigate,
                  level: 'list',
                }}
              />
              <View style={{ flex: 1 }}>
                <Machines {...{ ...p, ...props }} />
              </View>
            </View>
          )}
        </StackNavigator.Screen>
        <StackNavigator.Screen name={'MachinesGroup'} options={{ title: 'Machines Group' }}>
          {(p) => (
            <View style={{ width: '100%', flex: 1 }}>
              <Header
                {...{
                  toggleAddMachine,
                  toggleCreateMachinesGroup,
                  C,
                  navigate: navigation.navigate,
                  level: 'group',
                }}
              />
              <MachinesGroup {...{ ...p, ...props, toggleCreateMachinesGroup }} />
            </View>
          )}
        </StackNavigator.Screen>
        <StackNavigator.Screen name={'MachinesOperations'} options={{ title: 'Operations' }}>
          {(p) => (
            <View style={{ width: '100%', flex: 1 }}>
              <Header
                {...{
                  C,
                  navigate: navigation.navigate,
                  level: 'operations',
                }}
              />
              <MachinesOperations {...{ ...p, ...props }} />
            </View>
          )}
        </StackNavigator.Screen>
        <StackNavigator.Screen name={'MachinesImnoo'} options={{ title: 'Imnoo' }}>
          {(p) => (
            <View style={{ width: '100%', flex: 1 }}>
              <Header
                {...{
                  C,
                  navigate: navigation.navigate,
                  level: 'imnoo',
                }}
              />
              <MachinesImnoo {...{ ...p, ...props }} />
            </View>
          )}
        </StackNavigator.Screen>
      </StackNavigator.Navigator>
    </View>
  );
};

export default ColorWrapper(MachinesPage);
